import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {FiChevronDown, FiX} from 'react-icons/fi';
import {Checkbox} from '../../atoms/checkbox/checkbox';
import {FilterIcon} from '../../atoms/icons/filter-icon';
import {MagnifyingGlassIcon} from '../../atoms/icons/magnifying-glass';
import {
  AppliedFilterChip,
  AppliedFilterLabel,
  AppliedFilterRow,
  FilterAnchor,
  FilterBody,
  FilterCount,
  FilterEmpty,
  FilterFooter,
  FilterHeader,
  FilterOption,
  FilterOptionCopy,
  FilterOptions,
  FilterPanel,
  FilterSearch,
  FilterSection,
  FilterSectionTitle,
  FilterTextButton,
  FilterTrigger,
} from './filter-popover.styles';

export interface BeamFilterOption {
  value: string;
  label: string;
  content?: ReactNode;
  searchText?: string;
}

export interface BeamFilterSection {
  key: string;
  title: string;
  options: BeamFilterOption[];
  selectedValues: string[];
  searchPlaceholder?: string;
  emptyMessage?: string;
}

export interface BeamAppliedFilter {
  id: string;
  label: string;
}

interface BeamFilterPopoverProps {
  title: string;
  sections: BeamFilterSection[];
  activeCount: number;
  onToggle: (sectionKey: string, value: string) => void;
  onClear: () => void;
  disabled?: boolean;
  label?: string;
}

interface BeamAppliedFiltersProps {
  filters: BeamAppliedFilter[];
  resultLabel?: string;
  onRemove: (id: string) => void;
  onClear: () => void;
}

export const BeamFilterPopover = ({
  title,
  sections,
  activeCount,
  onToggle,
  onClear,
  disabled = false,
  label = 'Filter',
}: BeamFilterPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [sectionSearch, setSectionSearch] = useState<Record<string, string>>(
    {},
  );
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useRef(
    `beam-filter-${Math.random().toString(36).slice(2, 9)}`,
  );

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!anchorRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <FilterAnchor ref={anchorRef}>
      <FilterTrigger
        ref={triggerRef}
        type="button"
        $active={open || activeCount > 0}
        $open={open}
        aria-expanded={open}
        aria-controls={panelId.current}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}>
        <FilterIcon stroke="currentColor" />
        {label}
        {activeCount > 0 && <FilterCount>{activeCount}</FilterCount>}
        <FiChevronDown aria-hidden="true" />
      </FilterTrigger>

      {open && (
        <FilterPanel id={panelId.current} role="dialog" aria-label={title}>
          <FilterHeader>
            <strong>{title}</strong>
            <FilterTextButton type="button" onClick={() => setOpen(false)}>
              Cancel
            </FilterTextButton>
          </FilterHeader>
          <FilterBody>
            {sections.map((section) => {
              const search = sectionSearch[section.key] || '';
              const normalizedSearch = search.trim().toLocaleLowerCase();
              const options = normalizedSearch
                ? section.options.filter((option) =>
                    (option.searchText || option.label)
                      .toLocaleLowerCase()
                      .includes(normalizedSearch),
                  )
                : section.options;
              const showSearch = section.options.length > 6;

              return (
                <FilterSection key={section.key}>
                  <FilterSectionTitle>{section.title}</FilterSectionTitle>
                  {showSearch && (
                    <FilterSearch>
                      <input
                        type="search"
                        value={search}
                        aria-label={`Search ${section.title.toLocaleLowerCase()}`}
                        placeholder={
                          section.searchPlaceholder || `Search ${section.title}`
                        }
                        onInput={(event) => {
                          const value = event.currentTarget.value;
                          setSectionSearch((current) => ({
                            ...current,
                            [section.key]: value,
                          }));
                        }}
                      />
                      <MagnifyingGlassIcon />
                    </FilterSearch>
                  )}
                  <FilterOptions>
                    {options.length ? (
                      options.map((option) => {
                        const checked = section.selectedValues.includes(
                          option.value,
                        );
                        return (
                          <FilterOption key={option.value}>
                            <Checkbox
                              boxSize="small"
                              checked={checked}
                              aria-label={option.label}
                              onChange={() =>
                                onToggle(section.key, option.value)
                              }
                            />
                            <FilterOptionCopy>
                              {option.content || option.label}
                            </FilterOptionCopy>
                          </FilterOption>
                        );
                      })
                    ) : (
                      <FilterEmpty>
                        {section.emptyMessage || 'No options found.'}
                      </FilterEmpty>
                    )}
                  </FilterOptions>
                </FilterSection>
              );
            })}
          </FilterBody>
          <FilterFooter>
            <FilterTextButton
              type="button"
              $active={activeCount > 0}
              disabled={activeCount === 0}
              onClick={onClear}>
              Clear all
            </FilterTextButton>
          </FilterFooter>
        </FilterPanel>
      )}
    </FilterAnchor>
  );
};

export const BeamAppliedFilters = ({
  filters,
  resultLabel,
  onRemove,
  onClear,
}: BeamAppliedFiltersProps) => {
  const uniqueFilters = useMemo(
    () =>
      Array.from(
        new Map(filters.map((filter) => [filter.id, filter])).values(),
      ),
    [filters],
  );

  if (!uniqueFilters.length) return null;

  return (
    <AppliedFilterRow aria-label="Applied filters">
      {resultLabel && <AppliedFilterLabel>{resultLabel}</AppliedFilterLabel>}
      {uniqueFilters.map((filter) => (
        <AppliedFilterChip
          key={filter.id}
          type="button"
          aria-label={`Remove ${filter.label} filter`}
          onClick={() => onRemove(filter.id)}>
          {filter.label}
          <FiX size={14} aria-hidden="true" />
        </AppliedFilterChip>
      ))}
      <FilterTextButton type="button" $active onClick={onClear}>
        Clear all
      </FilterTextButton>
    </AppliedFilterRow>
  );
};
